import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import { DEFAULT_DAY_WIDTH_PX, LEFT_COLUMN_WIDTH_PX } from './timelineGeometry';
import {
  anchorDayAt,
  dayWidthToSlider,
  quantizeDayWidth,
  sliderToDayWidth,
  wheelDayWidth,
  zoomBounds,
  zoomedScrollLeft,
} from './timelineZoom';
import type { ZoomBounds } from './timelineZoom';

const STORAGE_KEY = 'pm.planning.timelineDayWidth';
const PERSIST_DEBOUNCE_MS = 300;
const ZOOM_BUTTON_STEP = 0.1;

const isStoredDayWidth = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 500;

/** Same guarded read as `lib/usePersistedState` — localStorage may throw. */
const readStoredDayWidth = (): number => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === null) return DEFAULT_DAY_WIDTH_PX;
    const parsed: unknown = JSON.parse(stored);
    return isStoredDayWidth(parsed) ? parsed : DEFAULT_DAY_WIDTH_PX;
  } catch {
    return DEFAULT_DAY_WIDTH_PX;
  }
};

export type TimelineZoomApi = {
  /** Effective px per day this render — the preference clamped to the viewport's bounds. */
  dayWidth: number;
  /** The slider's position for the current day width, 0 (year visible) … 1 (week). */
  sliderValue: number;
  /** Slider/keyboard path: zoom to a slider position, anchored on the viewport centre. */
  setSliderValue: (t: number) => void;
  /** The ± buttons: one fixed slider step in or out. */
  zoomBy: (direction: 1 | -1) => void;
};

/**
 * The timeline's zoom state: px per day as a globally persisted preference
 * (like the list/timeline view mode — a preference, not document state), with
 * Ctrl/Cmd+wheel and pinch zooming anchored on the date under the cursor.
 *
 * Bounds follow the measured track viewport, so "fully zoomed out" always
 * means about a year visible and "fully in" a week, whatever the panel width.
 * The stored preference is deliberately NOT clamped on resize — only the
 * effective value is — so narrowing a panel and widening it again returns to
 * the same zoom.
 *
 * Wheel listening is a native `{ passive: false }` listener: React's `onWheel`
 * registers passively and could not `preventDefault()` the browser's own
 * pinch-page-zoom. Anchoring writes `scrollLeft` in a layout effect keyed on
 * the day width — the DOM already has the new track width there — and re-runs
 * the month-band mirror inline, because the scroll event fires a frame later
 * and the band would shear for that frame.
 */
export const useTimelineZoom = (
  bodyScrollRef: RefObject<HTMLDivElement | null>,
  bandTrackRef: RefObject<HTMLDivElement | null>,
  dayCount: number,
  // A ref, not the value: `useBarDrag` needs this hook's dayWidth, so the
  // dragging id cannot exist yet when this hook runs. The caller mirrors it
  // into the ref after `useBarDrag` returns; wheel events read it live.
  draggingRef: RefObject<string | null>,
): TimelineZoomApi => {
  const [preferred, setPreferred] = useState(readStoredDayWidth);
  const [bounds, setBounds] = useState<ZoomBounds>(() => zoomBounds(0, dayCount));

  const dayWidth = quantizeDayWidth(preferred, bounds);

  // Live values for the stable wheel listener, mirrored per commit.
  const dayWidthRef = useRef(dayWidth);
  const boundsRef = useRef(bounds);
  useEffect(() => {
    dayWidthRef.current = dayWidth;
    boundsRef.current = bounds;
  });

  /** Set once by a zoom gesture, consumed by the layout effect below. */
  const pendingAnchor = useRef<{ day: number; viewportX: number } | null>(null);

  // Track viewport → bounds. ResizeObserver also fires once on observe, which
  // replaces the unmeasured fallback with real bounds on mount.
  useEffect(() => {
    const scroller = bodyScrollRef.current;
    if (!scroller) return;
    const observer = new ResizeObserver(() => {
      setBounds(zoomBounds(scroller.clientWidth - LEFT_COLUMN_WIDTH_PX, dayCount));
    });
    observer.observe(scroller);
    return () => {
      observer.disconnect();
    };
  }, [bodyScrollRef, dayCount]);

  // Debounced persist: wheel/pinch fires dozens of events a second, and a
  // synchronous localStorage write per tick janks the gesture.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferred));
      } catch {
        // Preference is lost for this session; the UI still works.
      }
    }, PERSIST_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [preferred]);

  const zoomTo = useCallback((next: number, anchor: { day: number; viewportX: number }) => {
    if (next === dayWidthRef.current) return;
    pendingAnchor.current = anchor;
    setPreferred(next);
  }, []);

  // Ctrl/Cmd + wheel (and trackpad pinch, which fires ctrlKey wheel events).
  useEffect(() => {
    const scroller = bodyScrollRef.current;
    if (!scroller) return;

    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return; // plain wheel keeps scrolling the page
      if (draggingRef.current !== null) return;
      event.preventDefault(); // stops the browser's own pinch-zoom of the page
      const deltaY = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      const next = wheelDayWidth(dayWidthRef.current, deltaY, boundsRef.current);
      const viewportX = Math.max(
        LEFT_COLUMN_WIDTH_PX,
        event.clientX - scroller.getBoundingClientRect().left,
      );
      zoomTo(next, {
        day: anchorDayAt(scroller.scrollLeft, viewportX, dayWidthRef.current),
        viewportX,
      });
    };

    scroller.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      scroller.removeEventListener('wheel', onWheel);
    };
  }, [bodyScrollRef, zoomTo]);

  // Restore the anchored day in the same frame the new track width paints.
  useLayoutEffect(() => {
    const anchor = pendingAnchor.current;
    const scroller = bodyScrollRef.current;
    if (!anchor || !scroller) return;
    pendingAnchor.current = null;
    const target = zoomedScrollLeft(anchor.day, anchor.viewportX, dayWidth);
    scroller.scrollLeft = Math.min(target, scroller.scrollWidth - scroller.clientWidth);
    // Mirror the month band now — waiting for the async scroll event would
    // shear the band against the rows for one frame.
    const band = bandTrackRef.current;
    if (band) band.style.transform = `translateX(${String(-scroller.scrollLeft)}px)`;
  }, [dayWidth, bodyScrollRef, bandTrackRef]);

  const setSliderValue = useCallback(
    (t: number) => {
      const scroller = bodyScrollRef.current;
      const next = sliderToDayWidth(t, boundsRef.current);
      if (!scroller) {
        setPreferred(next);
        return;
      }
      // Centre-anchored: idempotent across repeated steps, so continuous
      // slider onChange is safe.
      const viewportX = LEFT_COLUMN_WIDTH_PX + (scroller.clientWidth - LEFT_COLUMN_WIDTH_PX) / 2;
      zoomTo(next, {
        day: anchorDayAt(scroller.scrollLeft, viewportX, dayWidthRef.current),
        viewportX,
      });
    },
    [bodyScrollRef, zoomTo],
  );

  const sliderValue = dayWidthToSlider(dayWidth, bounds);

  const zoomBy = useCallback(
    (direction: 1 | -1) => {
      setSliderValue(Math.min(1, Math.max(0, sliderValue + direction * ZOOM_BUTTON_STEP)));
    },
    [setSliderValue, sliderValue],
  );

  return { dayWidth, sliderValue, setSliderValue, zoomBy };
};
